import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { FwIconModule, FwProgressModule, FwTooltipModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { downloadFile } from '../../shared/browser.utils';
import { JobLog, JobLogResponse } from '../../shared/models/job.model';

const TRUNCATION_START = 20; // show up to N lines before truncation
const TRUNCATION_END = 20; // show up to N lines after truncation

@Component({
  selector: 'job-log',
  templateUrl: './job-log.component.html',
  styleUrls: ['./job-log.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwIconModule,
    FwTooltipModule,
    FwProgressModule,
    MatTabsModule,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    ScrollingModule,
    SharedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobLogComponent implements AfterViewInit {
  jobId = input.required<string>()
  showHeader = input(false)

  virtualScroll = viewChild<CdkVirtualScrollViewport>(CdkVirtualScrollViewport)

  public showAllLines = signal(true)
  public logSections = signal<{
    all?: JobLog[],
    end?: JobLog[],
    start?: JobLog[],
  }>(null);

  private coreApi = inject(CoreApiService);

  constructor() {
    effect(() => {
      const jobId = this.jobId();
      if (jobId) {
        this.coreApi.jobs.logs.json(jobId).pipe(
          catchError((error: HttpErrorResponse): Observable<JobLogResponse> => {
            let msg = 'An error occurred while fetching the logs for this job.';
            if (error.status === 404) {
              msg = 'No logs were found for this job.';
            }
            return of({ _id: jobId, logs: [{ fd: -1, msg }] });
          }),
          map(json => {
            const lines: JobLog[] = json.logs.reduce((entries, entry) => {
              // Some entries contain newlines. Expand each line as their own entry.
              const messages = entry.msg.split(/\n(?!$)/);
              return entries
                .concat(messages.slice(0, -1).map(msg => ({ fd: entry.fd, msg: msg + '\n' })))
                .concat(messages.slice(-1).map(msg => ({ fd: entry.fd, msg })));
            }, []);
            if (lines.length > TRUNCATION_START + TRUNCATION_END) {
              this.showAllLines.set(false);
              return {
                all: lines,
                start: lines.slice(0, TRUNCATION_START),
                end: lines.slice(-TRUNCATION_END),
              };
            } else {
              this.showAllLines.set(true);
              return { all: lines, start: [], end: [] };
            }
          }),
        ).subscribe(sections => {
          this.logSections.set(sections);
        });
      }
    }, { allowSignalWrites: true });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.refreshVirtualScroll();
    });
  }

  refreshVirtualScroll(): void {
    const viewport = this.virtualScroll();
    if (viewport && this.showAllLines()) {
      viewport.checkViewportSize();
      viewport.scrollToIndex(0);
    }
  }

  onTabActivated(): void {
    setTimeout(() => {
      this.refreshVirtualScroll();
    });
  }

  downloadLogs(): void {
    const jobId = this.jobId();
    this.coreApi.jobs.logs.text(jobId).subscribe(response => {
      const filename = `${jobId}_logs.txt`;
      downloadFile(filename, 'data:text/plain;charset=utf-8,' + encodeURIComponent(response));
    });
  }
}
